import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { OtherCostCalculationTypeEnum, ProjectMethodologyEnum, SectionEnum } from 'gql/graphql';
import { useDebounce } from 'uikit';

import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { isUniqueGlobalLimitedCostGql, isUniqueGlobalOtherCostGql } from '../catalogOtherAndLimitedQueries';
import { CatalogOtherAndLimitedTab } from '../types';

export const useIsUniqueCatalogCost = ({
    tab,
    id,
    name,
    methodology,
    section,
    calculatingType,
}: {
    tab: CatalogOtherAndLimitedTab;
    id?: number;
    name: string;
    methodology?: ProjectMethodologyEnum;
    section: string;
    calculatingType: OtherCostCalculationTypeEnum;
}) => {
    const dispatch = useDispatch();
    const isOthersTab = tab === CatalogOtherAndLimitedTab.Others;
    const requestId = isOthersTab ? 'isUniqueGlobalOtherCost' : 'isUniqueGlobalLimitedCost';
    const trimmedName = name.trim();

    const [resetUniqueData] = useResetStoreData({ requestId });

    const [otherUniqueData, otherUniqueStatus, , otherUniqueRequest] = useRequest({
        requestId: 'isUniqueGlobalOtherCost',
        query: isUniqueGlobalOtherCostGql,
        withEffect: false,
    });
    const [limitedUniqueData, limitedUniqueStatus, , limitedUniqueRequest] = useRequest({
        requestId: 'isUniqueGlobalLimitedCost',
        query: isUniqueGlobalLimitedCostGql,
        withEffect: false,
    });

    useEffect(
        () => () => {
            resetUniqueData(undefined);
        },
        [resetUniqueData],
    );

    const debounceRequest = useDebounce(() => {
        if (!trimmedName || !methodology) {
            return;
        }

        if (isOthersTab) {
            dispatch(
                otherUniqueRequest({
                    id,
                    name: trimmedName,
                    section: section as SectionEnum,
                    methodology,
                    calculatingType,
                }),
            );

            return;
        }

        dispatch(
            limitedUniqueRequest({
                id,
                name: trimmedName,
                methodology,
            }),
        );
    }, 300);

    useEffect(() => {
        if (trimmedName && methodology) {
            debounceRequest();
        }
    }, [calculatingType, debounceRequest, id, isOthersTab, methodology, section, trimmedName]);

    const isUnique = isOthersTab
        ? otherUniqueData?.isUniqueGlobalOtherCost?.result
        : limitedUniqueData?.isUniqueGlobalLimitedCost?.result;
    const isUniqueStatus = isOthersTab ? otherUniqueStatus : limitedUniqueStatus;

    return useMemo(
        () => ({
            isUnique,
            isUniqueStatus,
            isUniqueFetching: isUniqueStatus === RequestStatus.FETCHING,
        }),
        [isUnique, isUniqueStatus],
    );
};
